import { Row, Col } from 'react-bootstrap';
import PropertyInfoCommercial from '../pdpComponents/PropertyInfo_Commercial';
import AmenitiesCard from './AmenitiesCard';

/**
 * Converts an amenityId string into a readable display name.
 * Uses propSubTypeId to strip the known prefix, then falls back to
 * stripping up to _SA_/_UA_ markers, or uses the last meaningful segments.
 *
 * Examples:
 *   ("MULTI_FAMILY_APARTMENTS_SA_24_HOURS_BUSINESS_CENTER", "MULTI_FAMILY_MID_RISE")
 *     → "24 Hours Business Center"
 *   ("HOSPITALITY_ECONOMY_BUSINESS_CENTER", "HOSPITALITY_ECONOMY")
 *     → "Business Center"
 *   ("OFFICE_HIGH_RISE_ELEVATOR", "OFFICE_HIGH_RISE")
 *     → "Elevator"
 *   ("RETAIL_MULTI_TENANT_FENCED_LOT", "RETAIL_MULTI_TENANT")
 *     → "Fenced Lot"
 */
function formatAmenityId(amenityId, propSubTypeId) {
  if (!amenityId) return '';

  let label = amenityId;

  // 1. Try stripping _SA_ or _UA_ marker (Multi-Family pattern)
  if (/_(?:SA|UA)_/.test(label)) {
    label = label.replace(/^.*?_(?:SA|UA)_/, '');
  }
  // 2. Try stripping the propSubTypeId prefix if provided
  else if (propSubTypeId && label.startsWith(propSubTypeId + '_')) {
    label = label.slice(propSubTypeId.length + 1);
  }
  // 3. Fallback: strip the first two segments (e.g. HOSPITALITY_ECONOMY_)
  else {
    const parts = label.split('_');
    if (parts.length > 2) {
      label = parts.slice(2).join('_');
    }
  }

  // Convert UPPER_SNAKE_CASE to Title Case
  return label
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * PropertyDetailsSection wrapper component for Commercial PDP.
 * Manages the two-column layout for Property Details grid and Amenities card.
 * On desktop (≥768px): side-by-side layout.
 * On mobile (<768px): stacked layout.
 */
const PropertyDetailsSection = ({ pdpData, pdpPropertyFees, emd, isFcOnlineFlag }) => {
  if (!pdpData) {
    return null;
  }

  const commercialFeatures = pdpData?.propertySpecialDetails?.commercialPropertyAdditionalFeatures;

  // Extract amenity data from commercialPropertyAdditionalFeatures
  // Unit and Site amenities come as comma-separated strings from the API
  const unitAmenitiesRaw = commercialFeatures?.multifamilyUnitAmenities || commercialFeatures?.amenitiesUnitAmenitiesMfl;
  const siteAmenitiesRaw = commercialFeatures?.multifamilySiteAmenities || commercialFeatures?.amenitiesSiteAmenitiesMfl;
  let unitAmenities = unitAmenitiesRaw ? unitAmenitiesRaw.split(',').map(item => item.trim()) : [];
  let siteAmenities = siteAmenitiesRaw ? siteAmenitiesRaw.split(',').map(item => item.trim()) : [];

  // Also extract from propertyAmenitiesBySubType (structured amenity objects from API)
  const amenitiesBySubType = commercialFeatures?.propertyAmenitiesBySubType;
  let propertyAmenities = [];
  if (amenitiesBySubType) {
    const allAmenities = Object.values(amenitiesBySubType).flat();
    const unitFromSubType = allAmenities
      .filter(a => a.amenityType === 'UNIT')
      .map(a => a.amenitiesDescription || formatAmenityId(a.amenityId, a.propSubTypeId));
    const siteFromSubType = allAmenities
      .filter(a => a.amenityType === 'SITE')
      .map(a => a.amenitiesDescription || formatAmenityId(a.amenityId, a.propSubTypeId));
    // General amenities (Hospitality, Office, Retail — no SITE/UNIT distinction)
    const generalFromSubType = allAmenities
      .filter(a => a.amenityType !== 'UNIT' && a.amenityType !== 'SITE')
      .map(a => a.amenitiesDescription || formatAmenityId(a.amenityId, a.propSubTypeId));

    // Merge without duplicates
    if (unitFromSubType.length > 0) {
      unitAmenities = [...new Set([...unitAmenities, ...unitFromSubType])];
    }
    if (siteFromSubType.length > 0) {
      siteAmenities = [...new Set([...siteAmenities, ...siteFromSubType])];
    }
    if (generalFromSubType.length > 0) {
      propertyAmenities = [...new Set(generalFromSubType)];
    }
  }

  // Industrial fields for amenities
  const subTypeFields = buildSubTypeFields(commercialFeatures);

  return (
    <div className="property-details-section">
      <Row>
        <Col xs={12} md={6}>
          <PropertyInfoCommercial
            pdpData={pdpData}
            pdpPropertyFees={pdpPropertyFees}
            emd={emd}
            status={pdpData?.propertyDetails?.status}
            getbidcenterdata={null}
            isFcOnlineFlag={isFcOnlineFlag}
          />
        </Col>
        <Col xs={12} md={6}>
          <AmenitiesCard
            unitAmenities={unitAmenities}
            siteAmenities={siteAmenities}
            propertyAmenities={propertyAmenities}
            subTypeFields={subTypeFields}
          />
        </Col>
      </Row>
    </div>
  );
};

/**
 * Builds industrial field arrays from commercialPropertyAdditionalFeatures
 * for display in the AmenitiesCard.
 */
function buildSubTypeFields(features) {
  if (!features) return [];

  const fields = [];

  if (features.industrialClearHeight) fields.push({ label: 'Clear Height', value: features.industrialClearHeight });
  if (features.industrialNumberOfLoadingDocks) fields.push({ label: 'Number of Loading Docks', value: features.industrialNumberOfLoadingDocks });
  if (features.industrialNumberOfDockDoors) fields.push({ label: 'Number of Dock Doors', value: features.industrialNumberOfDockDoors });
  if (features.industrialNumberOfDriveInDoors) fields.push({ label: 'Number of Drive-In Doors', value: features.industrialNumberOfDriveInDoors });
  if (features.industrialPowerCapacity) fields.push({ label: 'Power Capacity', value: features.industrialPowerCapacity });
  if (features.industrialColumnSpacing) fields.push({ label: 'Column Spacing', value: features.industrialColumnSpacing });
  if (features.industrialRailAccess) fields.push({ label: 'Rail Access', value: features.industrialRailAccess });
  if (features.industrialTenantUse) fields.push({ label: 'Tenant Use', value: features.industrialTenantUse });
  if (features.industrialPowerAmpsVoltage) fields.push({ label: 'Power Amps/Voltage', value: features.industrialPowerAmpsVoltage });
  if (features.industrialSprinklerType) fields.push({ label: 'Sprinkler Type', value: features.industrialSprinklerType });
  if (features.industrialTrailerParkingSpots) fields.push({ label: 'Trailer Parking Spots', value: features.industrialTrailerParkingSpots });

  // Office fields
  if (features.officeTenantIndustries) fields.push({ label: 'Tenant Industries', value: features.officeTenantIndustries });
  if (features.officeLeaseRolloverSchedule) fields.push({ label: 'Lease Rollover Schedule', value: features.officeLeaseRolloverSchedule });
  if (features.officeTiLcAssumptions) fields.push({ label: 'TI/LC Assumptions', value: features.officeTiLcAssumptions });
  if (features.officeFloorPlates) fields.push({ label: 'Floor Plates', value: features.officeFloorPlates });
  if (features.officeParkingRatio) fields.push({ label: 'Parking Ratio', value: features.officeParkingRatio });
  if (features.officeParkingType) fields.push({ label: 'Parking Type', value: features.officeParkingType });
  if (features.officeNumberOfUnits) fields.push({ label: 'Number of Units', value: features.officeNumberOfUnits });
  if (features.officeNumberOfElevators) fields.push({ label: 'Number of Elevators', value: features.officeNumberOfElevators });

  // Retail fields
  if (features.retailTenantRoster) fields.push({ label: 'Tenant Roster', value: features.retailTenantRoster });
  if (features.retailAnchorTenant) fields.push({ label: 'Anchor Tenant', value: features.retailAnchorTenant });
  if (features.retailLeaseType) fields.push({ label: 'Lease Type', value: features.retailLeaseType });
  if (features.retailTenantCreditQuality) fields.push({ label: 'Tenant Credit Quality', value: features.retailTenantCreditQuality });
  if (features.retailTrafficCounts) fields.push({ label: 'Traffic Counts', value: features.retailTrafficCounts });
  if (features.retailParkingRatio) fields.push({ label: 'Retail Parking Ratio', value: features.retailParkingRatio });
  if (features.retailShadowAnchor) fields.push({ label: 'Shadow Anchor', value: features.retailShadowAnchor });

  // Land fields
  if (features.landZoning) fields.push({ label: 'Zoning', value: features.landZoning });
  if (features.landEntitlements) fields.push({ label: 'Entitlements', value: features.landEntitlements });
  if (features.landUtilitiesAvailable) fields.push({ label: 'Utilities Available', value: features.landUtilitiesAvailable });
  if (features.landTrafficAndAccessPoints) fields.push({ label: 'Traffic & Access Points', value: features.landTrafficAndAccessPoints });
  if (features.landApprovedUses) fields.push({ label: 'Approved Uses', value: features.landApprovedUses });
  if (features.landFloodZone) fields.push({ label: 'Flood Zone', value: features.landFloodZone });

  // Hospitality fields
  if (features.hospitalityFranchise) fields.push({ label: 'Franchise', value: features.hospitalityFranchise === 'Y' ? 'Yes' : features.hospitalityFranchise });

  // Multifamily fields
  if (features.multifamilyNumberOfUnits) fields.push({ label: 'Number of Units', value: features.multifamilyNumberOfUnits });
  if (features.multifamilyUnitMix) fields.push({ label: 'Unit Mix', value: features.multifamilyUnitMix });
  if (features.multifamilyAverageUnitSize) fields.push({ label: 'Average Unit Size', value: features.multifamilyAverageUnitSize });
  if (features.multifamilyAverageRentByUnittype) fields.push({ label: 'Average Rent', value: features.multifamilyAverageRentByUnittype });
  if (features.multifamilyRenovationStatus) fields.push({ label: 'Renovation Status', value: features.multifamilyRenovationStatus });
  if (features.multifamilyYearOfMajorSystems) fields.push({ label: 'Year of Major Systems', value: features.multifamilyYearOfMajorSystems });
  if (features.multifamilyUnitAmenities) fields.push({ label: 'Unit Amenities', value: features.multifamilyUnitAmenities });
  if (features.multifamilySiteAmenities) fields.push({ label: 'Site Amenities', value: features.multifamilySiteAmenities });

  return fields;
}

export default PropertyDetailsSection;
